import {useState} from 'react';

import {SuggestionContentType, SuggestionItem} from './types';

export function extractSuggestionsOfType(
  suggestions: Array<SuggestionItem>,
  contentType: SuggestionContentType
) {
  return suggestions
    .filter((item) => item.content_type === contentType)
    .map((item) => item.name);
}

export const cleanStepId = (stepId?: string): string =>
  (stepId ?? '').replace(/stepid-/, '');

export function useHistoryData<T extends {source: string}>(
  initialValue?: T
): [Array<T>, (newEntry: T) => void, (entry: T) => void] {
  const [history, setHistory] = useState<Array<T>>([]);

  const appendHistory = (newEntry: T) => {
    setHistory((prevHistory) => [newEntry, ...prevHistory]);
  };

  const setCurrentMessage = (entry: T) => {
    setHistory((prevHistory) => {
      const newHistory = [...prevHistory];
      if (newHistory.length > 0 && newHistory[0].source === entry.source) {
        newHistory[0] = entry;
      } else {
        newHistory.unshift(entry); // In case the history is empty
      }
      return newHistory;
    });
  };

  if (initialValue != null && history.length === 0) {
    appendHistory(initialValue);
  }

  return [history, appendHistory, setCurrentMessage];
}

export const AI_POLICY_PROMPT = `
Write the content of this policy.

The content should delve into extensive detail across each section, meticulously
delineating the procedures, defining essential terms, and providing illustrative
examples, including what to do and what to avoid. Start the content with distinct
headings for 'Scope' and 'Audience' to ensure clarity and focus. Whenever referring to
the Company Name within the text ensure it is surrounded by <strong></strong> tags to maintain
consistency and emphasis.

Aim for precision in your instructions, weaving them into the narrative with clear
step-by-step guidance. Employ simple HTML to enhance the document's readability and
structure.

Thank you
`;

export const AI_PROCEDURE_PROMPT = `
Please create a minimum viable procedure for our company. This should include the following:

Name: A clear and concise name for the procedure
Description: A detailed description of the procedure, employing simple HTML markup to enhance
  readability and structure. Include headings, paragraphs, and any necessary formatting elements to make
  the description visually appealing and easy to understand.
  Headings in the description area must be h3 or below.
Steps: Add steps with names to define the procedure outline.
  Do not include any content for these steps. Avoid step numbering in the titles,
  our UI will handle numbering automatically.

The topic of the procedure should be aligned with the original procedure name (don't change the 
main purpose of the procedure)

The aim is to provide a skeleton for the procedure with precision and clarity.
The detailed description should weave instructions into a narrative with clear, step-by-step guidance.

Thank you
`;

export const AI_FULL_PROCEDURE_PROMPT = `
Please create a procedure for our company. This should include the following:

Name: A clear and concise name for the procedure
Description: A detailed description of the procedure, employing simple HTML markup to enhance
  readability and structure. Include headings, paragraphs, and any necessary formatting elements to make
  the description visually appealing and easy to understand.
  Headings in the description area must be h3 or below.
Steps: Add steps with names to define the procedure outline. Steps should have its contents filled.
  Try to describe each step in as much details as you can.
  Avoid step numbering in the titles,
  our UI will handle numbering automatically.
  
The topic of the procedure should be aligned with the original procedure name (don't change the 
main purpose of the procedure)

The detailed description should weave instructions into a narrative with clear, step-by-step guidance.

Thank you
`;

export const AI_SUGGESTION_PROMPT_DEFAULT = `Please suggest 3 names for procedures and 3 names for policies
 for our company in the "{industry}" industry. Provide the most relevant and beneficial document names that
 could enhance our business operations.`;

export const AI_SUGGESTION_PROMPT_PROCEDURES = `Please suggest 3 names for procedures for our company in the
 "{industry}" industry. Provide the most relevant and beneficial procedure names that could enhance our
 business operations.`;

export const AI_SUGGESTION_PROMPT_POLICIES = `Please suggest 3 names for policies for our company in
 the "{industry}" industry. Provide the most relevant and beneficial policy names that could enhance
 our business operations.`;

export const AI_SUGGESTION_PROMPT = {
  'policies and procedures': AI_SUGGESTION_PROMPT_DEFAULT,
  procedures: AI_SUGGESTION_PROMPT_PROCEDURES,
  policies: AI_SUGGESTION_PROMPT_POLICIES,
};
